// A scale bar that reads at a glance (spec §10): the first length of 1, 2 or 5 × 10ⁿ that is 80 to
// 200 CSS px long at the orbit target's depth (`hud.scalebar`), snapped as the screens snap it; in
// mm, or µm below 1 mm (the neuron scenes).

import { project, type Mat4 } from '../viewport/mat.ts';
import type { Vec3 } from '../viewport/api.ts';

/** CSS px per mm at distance `distanceMm`, for a vertical field of view over `heightPx`. */
export function mmPerPx(distanceMm: number, fovYDeg: number, heightPx: number): number {
  return (2 * distanceMm * Math.tan((fovYDeg * Math.PI) / 360)) / Math.max(1, heightPx);
}

/**
 * CSS px per mm at the orbit target, measured through the camera's viewProj: a step across the view
 * at the target's depth. Right however the projection spreads its field: the wgpu engine fits
 * `fovYDeg` to the clear area between the panels, not the canvas `height`.
 */
export function mmPerPxAt(viewProj: Mat4, eyeMm: Vec3, targetMm: Vec3, width: number, height: number): number {
  const f: Vec3 = [targetMm[0] - eyeMm[0], targetMm[1] - eyeMm[1], targetMm[2] - eyeMm[2]];
  // Across the view and level (view × superior); along x when looking straight down.
  const l = Math.hypot(f[0], f[1]);
  const across: Vec3 = l > 1e-6 ? [f[1] / l, -f[0] / l, 0] : [1, 0, 0];
  const step = Math.max(1e-3, Math.hypot(...f) / 100);
  const a = project(viewProj, targetMm, width, height);
  const b = project(viewProj, [targetMm[0] + across[0] * step, targetMm[1] + across[1] * step, targetMm[2]], width, height);
  return step / Math.hypot(b.x - a.x, b.y - a.y);
}

export function scaleBar(mmPerPixel: number, minPx = 80, maxPx = 200): { px: number; text: string; mm: number } {
  const steps: number[] = [];
  for (let e = -4; e <= 5; e++) for (const m of [1, 2, 5]) steps.push(m * 10 ** e);
  const px = (mm: number) => mm / mmPerPixel;
  // Outside any sensible zoom no step fits; then the one nearest the window's middle (in ratio).
  const mid = Math.sqrt(minPx * maxPx);
  const mm = steps.find((v) => px(v) >= minPx && px(v) <= maxPx) ?? steps.reduce((a, b) => (Math.abs(Math.log(px(b) / mid)) < Math.abs(Math.log(px(a) / mid)) ? b : a));
  const text = mm < 1 ? `${+(mm * 1000).toPrecision(3)}\u202Fµm` : `${+mm.toPrecision(3)}\u202Fmm`;
  return { px: px(mm), text, mm };
}
